import React, { useMemo, useState } from 'react';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './SucessBooking.css';

const PHONE_DISPLAY = '+39 342 911 8790';
const PHONE_LINK = '+393429118790';
const EMAIL = 'info@taximoroder.it';

const readStoredConfirmation = () => {
  try {
    return JSON.parse(sessionStorage.getItem('moroderBookingConfirmation'));
  } catch {
    return null;
  }
};

const SuccessBooking = ({ confirmation }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const booking = useMemo(() => confirmation || readStoredConfirmation(), [confirmation]);

  if (!booking?.bookingId) {
    return (
      <main className="confirmation-page">
        <section className="confirmation-card confirmation-unavailable">
          <h1>{t('confirmation_unavailable_title')}</h1>
          <p>{t('confirmation_unavailable_text')}</p>
          <button type="button" onClick={() => navigate('/')}>{t('confirmation_home')}</button>
        </section>
      </main>
    );
  }

  const amenityLabels = {
    child_seat: t('form_child_safety_seat'),
    bikes: t('form_bikes'),
    skis: t('form_skis'),
  };

  const copyReference = async () => {
    try {
      await navigator.clipboard.writeText(booking.bookingId);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <main className="confirmation-page">
      <section className="confirmation-card">
        <header className="confirmation-topbar">
          <span>{t('confirmation_kicker')}</span>
          <span className="confirmation-status"><i />{t('confirmation_status')}</span>
        </header>

        <div className="confirmation-hero">
          <div className="confirmation-check"><CheckRoundedIcon /></div>
          <div>
            <h1>{t('confirmation_received')}</h1>
            <p>{t('confirmation_email_sent')}</p>
          </div>
        </div>

        <div className="confirmation-layout">
          <section className="confirmation-panel confirmation-trip">
            <div className="confirmation-section-heading"><span>01</span><div><h2>{t('confirmation_booking_summary')}</h2><p>{t('confirmation_summary_hint')}</p></div></div>
            <dl>
              <div><dt><PlaceOutlinedIcon />{t('confirmation_start')}</dt><dd>{booking.origin}</dd></div>
              <div><dt><PlaceOutlinedIcon />{t('confirmation_destination')}</dt><dd>{booking.destination}</dd></div>
              <div className="confirmation-trip-meta">
                <span><dt><CalendarMonthOutlinedIcon />{t('booking_date')}</dt><dd>{booking.date}</dd></span>
                <span><dt><AccessTimeOutlinedIcon />{t('booking_time')}</dt><dd>{booking.time}</dd></span>
                <span><dt><PersonOutlineRoundedIcon />{t('booking_persons')}</dt><dd>{booking.persons}</dd></span>
              </div>
            </dl>
            {booking.amenities?.length > 0 && <div className="confirmation-extras"><span>{t('form_additional_details')}</span><p>{booking.amenities.map((item) => amenityLabels[item] || item).join(' · ')}</p></div>}
            <div className="confirmation-price"><span>{t('confirmation_price')}</span><strong>€ {Number(booking.price).toFixed(2)}</strong></div>
          </section>

          <aside className="confirmation-side">
            <section className="confirmation-panel confirmation-reference">
              <div className="confirmation-section-heading"><span>02</span><div><h2>{t('confirmation_reference')}</h2><p>{t('confirmation_reference_hint')}</p></div></div>
              <button type="button" onClick={copyReference} className="confirmation-copy"><code>{booking.bookingId}</code><ContentCopyRoundedIcon /></button>
              {copied && <span className="confirmation-copied" role="status">{t('confirmation_copied')}</span>}
              <div className="confirmation-customer"><strong>{booking.firstName} {booking.lastName}</strong><span>{booking.email}</span><span>{booking.phonenumber}</span></div>
            </section>

            <section className="confirmation-panel confirmation-contact">
              <div className="confirmation-section-heading"><span>03</span><div><h2>{t('confirmation_contact')}</h2><p>{t('confirmation_contact_hint')}</p></div></div>
              <div className="confirmation-contact-actions">
                <a href={`tel:${PHONE_LINK}`}><PhoneOutlinedIcon /><span>{PHONE_DISPLAY}</span></a>
                <a href={`https://wa.me/${PHONE_LINK.replace('+', '')}`} target="_blank" rel="noopener noreferrer"><WhatsAppIcon /><span>WhatsApp</span></a>
                <a href={`mailto:${EMAIL}`}><EmailOutlinedIcon /><span>{EMAIL}</span></a>
              </div>
            </section>
          </aside>
        </div>

        <div className="confirmation-footer">
          <p>{t('confirmation_driver_cancel_note')}</p>
          <button type="button" onClick={() => navigate('/')}><HomeOutlinedIcon />{t('confirmation_home')}</button>
        </div>
      </section>
    </main>
  );
};

export default SuccessBooking;
